import {
  ColumnDef,
  functionalUpdate,
  GroupingState,
  InitialTableState,
  Row,
  RowSelectionState,
  SortingState,
  TableOptions,
  Updater,
} from '@tanstack/react-table';
import { isEmpty } from 'lodash-es';
import { CSSProperties, useCallback, useMemo, useRef, useState } from 'react';
import { useSingleTableShortcutSelection } from 'src/components/Events/EventPage/hooks/useSingleTableShortcutSelection';
import { useAppContext } from 'src/contexts/AppContext';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import {
  MultiSelectScope,
  NO_GROUP_ID,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { useClearShiftSelectionOnSortingChange } from 'src/contexts/MultiSelectionContext/useClearShiftSelectionOnSortingChange';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { NoData, Table } from 'src/tables/Table';
import {
  defaultOnSaleEventsColumnsConfig,
  filterColumnsByFeatures,
  getOnSaleEventsColumnConfigById,
} from 'src/utils/columns/columnUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { SectionType } from 'src/utils/types/sectionType';
import { hasFeatures } from 'src/utils/userUtils';
import { SomethingWentWrong } from 'src/views';
import { UserSetting } from 'src/WebApiController';

import { filterCustomColumnsForOnSaleEvent } from './OnSaleDiscoveryCustomColumnUtils';
import {
  customColumnDef,
  ON_SALE_EVENTS_TABLE_COLUMNS_CONFIG,
} from './OnSaleDiscoveryView.constants';
import {
  CustomOnSaleEventColumn,
  OnSaleEventTableColumnId,
  OnSaleEventWithMetrics,
} from './OnSaleDiscoveryView.types';

export type OnSaleEventTableProps = {
  highlightedId?: string;
  failedToRetrieveData: boolean;
  onMount?: () => void;
  onUnmount?: (state: object) => void;
  initState?: InitialTableState;
  tableHeadStyle?: CSSProperties;
  isLoading?: boolean;
  events: OnSaleEventWithMetrics[];
  onEventsRendered: (ids: number[]) => void;
};

const getRowId = (originalRow: OnSaleEventWithMetrics | null): string => {
  return (originalRow as OnSaleEventWithMetrics)?.event?.virtId ?? '';
};

export const OnSaleDiscoveryTable = ({
  highlightedId,
  failedToRetrieveData,
  initState,
  tableHeadStyle,
  events,
  isLoading,
  onEventsRendered,
}: OnSaleEventTableProps) => {
  const { appContext, loginContext } = useAppContext();
  const { getSelection, selectionMode, setGroupItems } =
    useMultiSelectionContext();
  const eventSelection = getSelection();

  const viewedEventIdsRef = useRef<number[]>([]);
  const buttonClickedRef = useRef<boolean>();

  const eventsText = useContent(ContentId.Events);

  // Enable passing in table state as parameters -- we can remount with the last state the user was on
  const [sorting, setSorting] = useState<SortingState>(
    initState?.sorting || []
  );
  const [grouping, setGrouping] = useState<GroupingState>(
    initState?.grouping || []
  );

  useClearShiftSelectionOnSortingChange({ sortingState: sorting });

  const {
    value: storedEventsColumnOrderSetting = defaultOnSaleEventsColumnsConfig,
  } = useServerUserSetting<OnSaleEventTableColumnId[]>({
    id: UserSetting.OnSaleEventColumnsOrder,
  });
  const { value: storedEventsColumnsEnabledSetting } = useServerUserSetting<
    string[]
  >({
    id: UserSetting.OnSaleEventColumnsEnabled,
  });
  const { value: customOnSaleEventColumns = [] } = useServerUserSetting<
    CustomOnSaleEventColumn[]
  >({
    id: UserSetting.OnSaleEventCustomColumns,
  });

  const customOnSaleEventColumnsFiltered = filterCustomColumnsForOnSaleEvent(
    customOnSaleEventColumns
  );

  const displayedColumnsConfig = useMemo(() => {
    const eventsColumnOrder = storedEventsColumnOrderSetting.filter(
      // If storedEventsColumnsEnabledSetting has never been set, default to true
      (c) => storedEventsColumnsEnabledSetting?.includes(c) ?? true
    );

    const columns = new Set([
      // Add unconfigurable columns first
      ...Object.values(ON_SALE_EVENTS_TABLE_COLUMNS_CONFIG)
        .filter((column) => {
          const columnDef = getOnSaleEventsColumnConfigById(
            column.id as OnSaleEventTableColumnId
          ).personalization;
          const hasFeatureForColumn =
            columnDef.requiredFeatures.length === 0 ||
            hasFeatures(
              loginContext?.user,
              appContext?.features,
              columnDef.requiredFeatures,
              columnDef.isReverseFeatureCheck
            );

          const isConfigurable = columnDef.isConfigurable;
          return !isConfigurable && hasFeatureForColumn;
        })
        .map((item) => item.id as OnSaleEventTableColumnId),
      // Add user defined columns next
      ...filterColumnsByFeatures(
        eventsColumnOrder,
        SectionType.OnSaleEvents,
        customOnSaleEventColumnsFiltered,
        loginContext?.user,
        appContext?.features
      ),
    ]);

    return Array.from(columns).reduce<
      ColumnDef<OnSaleEventWithMetrics | null>[]
    >((acc, columnId) => {
      if (customOnSaleEventColumnsFiltered.find((c) => c.id === columnId)) {
        acc.push(
          customColumnDef(
            columnId,
            customOnSaleEventColumnsFiltered.find((c) => c.id === columnId)
              ?.formula
          )
        );
      } else {
        const columnConfig = Object.values(
          ON_SALE_EVENTS_TABLE_COLUMNS_CONFIG
        ).find((column) => column.id === columnId);
        if (columnConfig) acc.push(columnConfig);
      }

      return acc;
    }, []);
  }, [
    appContext?.features,
    customOnSaleEventColumnsFiltered,
    loginContext?.user,
    storedEventsColumnOrderSetting,
    storedEventsColumnsEnabledSetting,
  ]);

  const rowSelection = useMemo(() => {
    if (!selectionMode && highlightedId) {
      return {
        [highlightedId]: true,
      };
    }
    return eventSelection.items.itemIds.reduce<Record<string, boolean>>(
      (acc, eventId) => {
        acc[eventId] = true;
        return acc;
      },
      {}
    );
  }, [selectionMode, highlightedId, eventSelection.items.itemIds]);

  const showCheckbox = selectionMode?.mode === MultiSelectScope.AllGroups;

  const { lastSelectedRowRefIndex, isShiftKeyPressed } =
    useSingleTableShortcutSelection<OnSaleEventWithMetrics>({});

  const onRowSelectionChanged = useCallback(
    (updater: Updater<RowSelectionState>) => {
      if (isShiftKeyPressed) {
        return;
      }
      const newSelections =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      setGroupItems(NO_GROUP_ID, newSelections);
    },
    [isShiftKeyPressed, rowSelection, setGroupItems]
  );

  const onRowShiftSelect = useCallback(
    (
      tableRows: Row<OnSaleEventWithMetrics | null>[] | undefined,
      row: Row<OnSaleEventWithMetrics | null>,
      index: number
    ) => {
      if (!tableRows || buttonClickedRef.current) {
        return;
      }

      const rowId = row.id.toString();
      const newIsSelected = !rowSelection[rowId];

      if (isShiftKeyPressed && lastSelectedRowRefIndex.current !== null) {
        const start = Math.min(lastSelectedRowRefIndex.current, index);
        const end = Math.max(lastSelectedRowRefIndex.current, index);

        const newRowSelection = { ...rowSelection };

        for (let i = start; i <= end; i++) {
          const row = tableRows[i];
          if (row) {
            newRowSelection[row.id] = newIsSelected;
          }
        }

        lastSelectedRowRefIndex.current = index;
        setGroupItems(NO_GROUP_ID, newRowSelection);
      } else {
        lastSelectedRowRefIndex.current = index;
      }

      buttonClickedRef.current = true;
      // Avoid double click in strict mode
      setTimeout(() => (buttonClickedRef.current = false), 300);
    },
    [isShiftKeyPressed, lastSelectedRowRefIndex, rowSelection, setGroupItems]
  );

  const options: Partial<TableOptions<OnSaleEventWithMetrics | null>> = useMemo(
    () => ({
      data: events,
      columns: displayedColumnsConfig,
      state: {
        expanded: true,
        grouping,
        sorting,
        rowSelection,
        columnVisibility: {
          checkbox: showCheckbox,
        },
        columnPinning: {
          right: [OnSaleEventTableColumnId.Action],
        },
      },
      onGroupingChange: setGrouping,
      getRowId: getRowId,
      onRowSelectionChange: onRowSelectionChanged,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSortingChange: (sortingUpdaterFn: any) => {
        const newSortVal = functionalUpdate(sortingUpdaterFn, sorting);
        setSorting(newSortVal);
      },
      enableRowSelection: showCheckbox,
      enableMultiRowSelection: showCheckbox,
      enableGrouping: true,
    }),
    [
      events,
      displayedColumnsConfig,
      grouping,
      sorting,
      rowSelection,
      showCheckbox,
      onRowSelectionChanged,
    ]
  );

  const allEventMetricsLoaded = useMemo(
    () => events.every((event) => event.metrics != null),
    [events]
  );

  const onItemsRendered = useCallback(
    (rows: Row<OnSaleEventWithMetrics | null>[]) => {
      // This callback is rendered on the change of what user sees in the vituosso
      // which can be changed with the sorting, even before all metrics are loaded
      // Avoid logging sentry spans when event are not fully loaded
      if (!allEventMetricsLoaded) return;

      const viewedEventIds = viewedEventIdsRef.current;
      // Ensure to only log the different event ids
      const renderedEventIds = rows
        .map((row) => row?.original?.event?.viagId ?? null)
        .filter((id: number | null): id is number => id != null)
        .filter((id) => !viewedEventIds.includes(id));
      if (isEmpty(renderedEventIds)) return;

      onEventsRendered(renderedEventIds);
    },
    [allEventMetricsLoaded, onEventsRendered]
  );

  if (failedToRetrieveData) {
    return (
      <SomethingWentWrong
        message={<Content id={ContentId.FailToLoadListContent} />}
      />
    );
  }

  if (isLoading) {
    return <PosSpinner />;
  }

  return events.length > 0 ? (
    <Table
      options={options}
      tableLayout="fixed"
      tableHeadStyle={{ top: '56px', ...tableHeadStyle }}
      useVirtuoso
      useDataGrouping
      withOuterPadding
      usePaginationFooter={false}
      itemsRendered={onItemsRendered}
      onRowShiftSelect={onRowShiftSelect}
    />
  ) : (
    // We should never see this - because the events are filtered to only those that has sales
    // So this is to prevent a crash - but this should be looked at
    <NoData>
      <FormatContent id={FormatContentId.NoDataAvailable} params={eventsText} />
    </NoData>
  );
};
